import union from '@turf/union';
import {
  polygon as turfPolygon,
  type Feature,
  type MultiPolygon,
  type Polygon,
} from '@turf/helpers';
import type { Piece, Pieces } from 'components/geometry-editing';

/**
 * Ensure a ring is explicitly closed, as turf requires.
 *
 * @param {Array} ring - Ring of `[lon, lat]` positions.
 * @return {Array} The closed ring.
 */
const closeRing = (ring: GeoJSON.Position[]): GeoJSON.Position[] => {
  if (ring.length < 3) return ring;
  const [first] = ring;
  const last = ring[ring.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
};

/**
 * The boundary pieces as a single GeoJSON geometry — one Polygon, or a
 * MultiPolygon when the boundary is several disjoint rings. Null when there is no
 * usable ring (so the caller falls back to the survey source, or refuses).
 *
 * @param {Pieces} pieces - The boundary rings.
 * @return {GeoJSON.Polygon | GeoJSON.MultiPolygon | null} The geometry.
 */
export const piecesToGeojson = (
  pieces: Pieces
): GeoJSON.Polygon | GeoJSON.MultiPolygon | null => {
  const rings = pieces
    .filter((piece) => piece.length >= 3)
    .map((piece) => closeRing(piece));
  if (rings.length === 0) return null;
  if (rings.length === 1) return { type: 'Polygon', coordinates: [rings[0]] };
  return { type: 'MultiPolygon', coordinates: rings.map((ring) => [ring]) };
};

/**
 * Outer rings of a polygon or multipolygon, ignoring holes.
 *
 * @param {object} geometry - The geometry.
 * @return {Array} The outer rings.
 */
const outerRings = (geometry: GeoJSON.Geometry): GeoJSON.Position[][] => {
  if (geometry.type === 'Polygon') return [geometry.coordinates[0]];
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map((p) => p[0]);
  }
  return [];
};

/**
 * How far apart two rings' corners may be and still be treated as the same
 * corner, in metres.
 *
 * Sized for survey data, not for drawing: a section side is ~1,600 m, so a metre
 * is far below anything a surveyed boundary distinguishes, while being enough to
 * absorb the disagreement between a unit polygon stored in `development_areas`
 * and the `reference_locations` tract it was derived from. Corners further apart
 * than this are genuinely different corners and are left alone.
 */
/**
 * How far a ring's corner may sit from another ring and still be treated as
 * lying on it, in metres.
 *
 * Sized for the disagreement between two independently stored polygons, not for
 * drawing: a section side is ~1,600 m, so ten metres is 0.6% of one — below what
 * a surveyed boundary distinguishes, and enough to absorb a unit polygon in
 * `development_areas` not reproducing the `reference_locations` tract it abuts.
 * Shapes further apart than this are genuinely not touching, and are left to be
 * reported as separate shapes.
 */
const WELD_TOLERANCE_M = 10;

const M_PER_DEG_LAT = 111_320;

/**
 * Pull each ring's corners onto the rings before it — onto a corner where one is
 * close, otherwise onto the nearest point along an EDGE — when within
 * {@link WELD_TOLERANCE_M}.
 *
 * Two shapes only dissolve on union if their shared edge is described by
 * IDENTICAL coordinates. Two survey tracts do: they come from the same table and
 * the server unions them, so they already share vertices — which is why survey to
 * survey merges cleanly. A unit does not. Its polygon lives in
 * `development_areas` and was stored independently of the `reference_locations`
 * tract beside it, so a sliver survives, the union reports more than one shape
 * and the unit cannot be saved even though the two are flush on screen.
 *
 * Projecting onto edges is what makes that case work. Corner-to-corner welding
 * only helps when both rings happen to have a corner at the same place; along a
 * shared edge they usually do not, because the unit's edge is one long segment
 * and the tract's ends elsewhere. Snapping the corner onto the segment gives the
 * union the coincident geometry it needs.
 *
 * Deliberately not a grid snap: a grid has to be an order of magnitude coarser
 * than the gap to close it, which moves every coordinate in the boundary. This
 * moves a corner only where another ring is already within ten metres, so
 * geometry with no neighbour is left exactly as the server sent it.
 *
 * @param {Pieces} rings - Rings of `[lng, lat]` positions.
 * @return {Pieces} The rings, with near-coincident geometry made identical.
 */
const weldRings = (rings: Pieces): Pieces => {
  if (rings.length < 2) return rings;

  const anchors: Pieces = [];
  const welded: Pieces = [];

  for (const ring of rings) {
    const next = ring.map((position) => {
      const [lng, lat] = position;
      const mPerDegLon = M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
      const metres = (to: GeoJSON.Position): number =>
        Math.hypot((to[0] - lng) * mPerDegLon, (to[1] - lat) * M_PER_DEG_LAT);

      let best: GeoJSON.Position | null = null;
      let bestDistance = WELD_TOLERANCE_M;

      for (const anchor of anchors) {
        for (let i = 0; i < anchor.length; i += 1) {
          const a = anchor[i];
          const b = anchor[(i + 1) % anchor.length];

          // A corner wins outright when it is in range: welding to an existing
          // point keeps the ring's vertex count down and is what two shapes that
          // really do share a corner should do.
          const atCorner = metres(a);
          if (atCorner <= bestDistance) {
            bestDistance = atCorner;
            best = a;
          }

          // Otherwise the closest point along the edge, clamped so it cannot
          // land past either end of a segment that does not run there.
          const ax = (a[0] - lng) * mPerDegLon;
          const ay = (a[1] - lat) * M_PER_DEG_LAT;
          const bx = (b[0] - lng) * mPerDegLon;
          const by = (b[1] - lat) * M_PER_DEG_LAT;
          const abx = bx - ax;
          const aby = by - ay;
          const lengthSq = abx * abx + aby * aby;
          if (lengthSq === 0) continue;
          const t = Math.min(1, Math.max(0, -(ax * abx + ay * aby) / lengthSq));
          const distance = Math.hypot(ax + abx * t, ay + aby * t);
          if (distance < bestDistance) {
            bestDistance = distance;
            best = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
          }
        }
      }

      return best ?? position;
    });
    welded.push(next);
    anchors.push(next);
  }

  return welded;
};

/**
 * Perpendicular distance below which a vertex is treated as lying ON the line
 * between its neighbours, in metres.
 *
 * Matched to {@link WELD_TOLERANCE_M}: welding two shapes flush can leave a jog
 * of up to that size where their shared edge did not line up exactly, and a jog
 * is not a corner of the unit.
 */
const SIMPLIFY_TOLERANCE_M = 1;

/**
 * Drop vertices that do not change the shape — duplicates, and points within
 * {@link SIMPLIFY_TOLERANCE_M} of the line joining their neighbours.
 *
 * Matters most for the setback. `unitSetbackInnerRings` grows a strip per edge
 * and OVERHANGS each end by the setback distance so neighbouring strips meet
 * squarely at corners. A metre-long jog is past its 1 ft guard, so it earns a
 * full-size strip pointing along the jog's own normal and overhanging hundreds
 * of feet at both ends — carving a wedge out of the keep-out band that no real
 * edge of the unit justifies.
 *
 * @param {Piece} ring - Ring of `[lng, lat]` positions.
 * @return {Piece} The ring without its negligible vertices.
 */
const cleanRing = (ring: Piece): Piece => {
  if (ring.length < 4) return ring;

  const open =
    ring[0][0] === ring[ring.length - 1][0] &&
    ring[0][1] === ring[ring.length - 1][1]
      ? ring.slice(0, -1)
      : ring.slice();
  if (open.length < 4) return ring;

  const kept: GeoJSON.Position[] = [];
  for (let i = 0; i < open.length; i += 1) {
    const prev =
      kept.length > 0 ? kept[kept.length - 1] : open[open.length - 1];
    const here = open[i];
    const next = open[(i + 1) % open.length];

    const mPerDegLon = M_PER_DEG_LAT * Math.cos((here[1] * Math.PI) / 180);
    const toXY = (p: GeoJSON.Position): [number, number] => [
      (p[0] - here[0]) * mPerDegLon,
      (p[1] - here[1]) * M_PER_DEG_LAT,
    ];
    const [ax, ay] = toXY(prev);
    const [bx, by] = toXY(next);
    const span = Math.hypot(bx - ax, by - ay);
    // `here` is at the origin, so its distance to the prev→next line is the
    // triangle's height on that base.
    const deviation =
      span === 0 ? Math.hypot(ax, ay) : Math.abs(ax * by - ay * bx) / span;

    if (deviation > SIMPLIFY_TOLERANCE_M) kept.push(here);
  }

  // Never simplify a ring out of existence.
  if (kept.length < 3) return ring;
  return [...kept, kept[0]];
};

/**
 * Dissolve a set of rings into their merged outer boundary — the union's outer
 * ring(s), with shared internal borders removed. Mirrors the mock's `mergePolys`.
 *
 * A degenerate/brittle `union` keeps the accumulator so the result never blanks;
 * with one usable ring it returns that ring unchanged.
 *
 * @param {Pieces} rings - Rings of `[lng, lat]` positions.
 * @return {Pieces} The merged outer rings.
 */
export const mergeOuterRings = (rings: Pieces): Pieces => {
  const polys = weldRings(rings)
    .filter((ring) => ring.length >= 3)
    .map(
      (ring) =>
        turfPolygon([closeRing(ring)]) as Feature<Polygon | MultiPolygon>
    );
  if (polys.length === 0) return [];

  let acc: Feature<Polygon | MultiPolygon> = polys[0];
  for (let i = 1; i < polys.length; i += 1) {
    try {
      acc = (union(acc, polys[i]) as Feature<Polygon | MultiPolygon>) ?? acc;
    } catch {
      // Brittle polyclip on this pair — keep what we have and move on.
    }
  }
  return outerRings(acc.geometry).map(cleanRing);
};

/**
 * The unit boundary implied by a unit's own footprint plus the survey areas
 * picked on top of it — a UNION of the parts, returning every disjoint outer ring
 * so each stays editable.
 *
 * The unit's footprint stays in the union deliberately. It is what the light-blue
 * working fill covers, what the vertex handles hang off, and what the
 * multipolygon check counts — so dropping it when a survey is picked took the
 * editable boundary off the unit entirely. The mock keeps the footprint by
 * PREFILLING one `custom` row per intersecting tract; the picker is deliberately
 * not prefilled here, so the ring is unioned in directly instead. Same geometry,
 * no phantom rows.
 *
 * `surveyPieces` is the SERVER's carved geometry
 * (`ST_Union(quartered_geography(...))` in `SurveyGeometryBuilder`, via
 * `useAreaPreview`), so quarter calls are already applied and `mergeOuterRings`
 * dissolves whatever touches. Pieces that genuinely do not touch stay separate,
 * and the panel refuses to save them (see `unitPolygonFrom`).
 *
 * With no survey pieces this is the base ring alone, which is what makes removing
 * the last survey row revert the boundary to the unit. With neither it is empty —
 * a brand-new unit has no shape until a tract lands.
 *
 * Per-row `base ∩ tract` clipping (the mock's deferred "Draw custom shape" mode)
 * belongs here when it lands.
 *
 * @param {Piece | null} baseRing - The unit's own footprint, or null when there
 *   is no unit yet (a brand-new area).
 * @param {Pieces} surveyPieces - Server-composed geometry for the picked tracts.
 * @return {Pieces} The composed boundary.
 */
export const composeUnitBoundary = (
  baseRing: Piece | null,
  surveyPieces: Pieces
): Pieces => {
  const base = baseRing && baseRing.length >= 3 ? [baseRing] : [];
  const parts = [...base, ...surveyPieces];
  return parts.length > 0 ? mergeOuterRings(parts) : [];
};
